import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { buildComposerExtensions, buildComposerEditorProps } from './richTextSchema';
import {
    Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
    List, ListOrdered, Quote, Minus, Link as LinkIcon, Image as ImageIcon,
    AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Heading3,
    Undo, Redo, Unlink, Sparkles, Library, RectangleHorizontal, X
} from 'lucide-react';
import { cn } from '../lib/utils';
import MergeVariableDropdown from './MergeVariableDropdown';
import AIEmailWriter from './AIEmailWriter';
import ContentPicker, { getVideoEmbedUrl } from './ContentPicker';
import VoiceButton from './ui/VoiceButton';

// Toolbar Button Component
function ToolbarButton({ onClick, isActive, disabled, children, title }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={cn(
                'p-1.5 rounded transition-colors',
                isActive ? 'bg-stone-100 text-stone-900' : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700',
                disabled && 'opacity-50 cursor-not-allowed'
            )}
        >
            {children}
        </button>
    );
}

// Divider Component
function ToolbarDivider() {
    return <div className="w-px h-6 bg-stone-200 mx-1" />;
}

// ─── Email Button Config Popover ────────────────────────────────────────────

const BUTTON_PRESETS = [
    { label: 'Dark', bg: '#1c1917', tc: '#ffffff' },
    { label: 'Blue', bg: '#2563eb', tc: '#ffffff' },
    { label: 'Green', bg: '#16a34a', tc: '#ffffff' },
    { label: 'Orange', bg: '#ea580c', tc: '#ffffff' },
    { label: 'Purple', bg: '#7c3aed', tc: '#ffffff' },
    { label: 'Red', bg: '#dc2626', tc: '#ffffff' },
];

function EmailButtonPopover({ editor, onClose }) {
    const isEditing = editor.isActive('emailButton');
    const currentAttrs = isEditing ? editor.getAttributes('emailButton') : {};

    const [text, setText] = useState(currentAttrs.text || 'Click Here');
    const [href, setHref] = useState(currentAttrs.href || 'https://');
    const [bg, setBg] = useState(currentAttrs.backgroundColor || '#1c1917');
    const [tc, setTc] = useState(currentAttrs.textColor || '#ffffff');
    const [radius, setRadius] = useState(currentAttrs.borderRadius || '6');
    const [alignment, setAlignment] = useState(currentAttrs.alignment || 'center');
    const [width, setWidth] = useState(currentAttrs.width || 'auto');

    const popRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (popRef.current && !popRef.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [onClose]);

    const handleApply = () => {
        const attrs = { text, href, backgroundColor: bg, textColor: tc, borderRadius: radius, alignment, width };
        if (isEditing) {
            editor.commands.updateEmailButton(attrs);
        } else {
            editor.commands.insertEmailButton(attrs);
        }
        onClose();
    };

    const handleRemove = () => {
        if (isEditing) {
            editor.commands.deleteSelection();
        }
        onClose();
    };

    return (
        <div ref={popRef}
            className="absolute top-full left-0 mt-1 z-50 w-72 bg-white border border-stone-200 rounded-xl shadow-xl p-3 space-y-3"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {isEditing ? 'Edit Button' : 'Insert Button'}
                </span>
                <button onClick={onClose} className="p-0.5 text-stone-400 hover:text-stone-600">
                    <X className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Button Text */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Button Text</label>
                <input value={text} onChange={e => setText(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400"
                    placeholder="Click Here" />
            </div>

            {/* URL */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Link URL</label>
                <input value={href} onChange={e => setHref(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400 font-mono"
                    placeholder="https://example.com" />
            </div>

            {/* Color Presets */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Color</label>
                <div className="flex gap-1.5 mb-2">
                    {BUTTON_PRESETS.map(p => (
                        <button key={p.label} type="button"
                            onClick={() => { setBg(p.bg); setTc(p.tc); }}
                            className={`w-7 h-7 rounded-lg border-2 transition-all ${bg === p.bg ? 'border-stone-900 scale-110' : 'border-transparent'}`}
                            style={{ backgroundColor: p.bg }}
                            title={p.label}
                        />
                    ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center gap-1.5">
                        <input type="color" value={bg} onChange={e => setBg(e.target.value)}
                            className="w-6 h-6 rounded cursor-pointer border border-stone-200" />
                        <input value={bg} onChange={e => setBg(e.target.value)}
                            className="flex-1 px-2 py-1 text-[10px] border border-stone-200 rounded font-mono" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <input type="color" value={tc} onChange={e => setTc(e.target.value)}
                            className="w-6 h-6 rounded cursor-pointer border border-stone-200" />
                        <span className="text-[10px] text-stone-400">Text</span>
                    </div>
                </div>
            </div>

            {/* Shape & Width */}
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Shape</label>
                    <select value={radius} onChange={e => setRadius(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-lg">
                        <option value="0">Square</option>
                        <option value="6">Rounded</option>
                        <option value="12">More Rounded</option>
                        <option value="100">Pill</option>
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] font-medium text-stone-500 mb-1">Width</label>
                    <select value={width} onChange={e => setWidth(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-stone-200 rounded-lg">
                        <option value="auto">Auto</option>
                        <option value="full">Full Width</option>
                    </select>
                </div>
            </div>

            {/* Alignment */}
            <div>
                <label className="block text-[10px] font-medium text-stone-500 mb-1">Alignment</label>
                <div className="flex gap-1">
                    {['left', 'center', 'right'].map(a => (
                        <button key={a} type="button"
                            onClick={() => setAlignment(a)}
                            className={`flex-1 py-1.5 text-[10px] font-medium rounded-lg capitalize ${
                                alignment === a ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-500'
                            }`}>
                            {a}
                        </button>
                    ))}
                </div>
            </div>

            {/* Live Preview */}
            <div className="bg-stone-50 rounded-lg p-3 border border-stone-100">
                <p className="text-[10px] text-stone-400 mb-2">Preview</p>
                <div style={{ textAlign: alignment }}>
                    <a href="#preview" onClick={e => e.preventDefault()}
                        style={{
                            display: width === 'full' ? 'block' : 'inline-block',
                            padding: '10px 24px',
                            backgroundColor: bg,
                            color: tc,
                            borderRadius: `${radius}px`,
                            textDecoration: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            fontFamily: 'Arial, sans-serif',
                            textAlign: 'center',
                        }}>
                        {text || 'Click Here'}
                    </a>
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
                <button onClick={handleApply}
                    className="flex-1 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800 transition-colors">
                    {isEditing ? 'Update Button' : 'Insert Button'}
                </button>
                {isEditing && (
                    <button onClick={handleRemove}
                        className="px-3 py-1.5 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                        Remove
                    </button>
                )}
            </div>
        </div>
    );
}

// ─── Toolbar presets ────────────────────────────────────────────────────────
//
// The toolbar used to be a hand-written run of 23 buttons gated only on
// `editable`. Every surface therefore got the campaign/sequence authoring bar,
// including a reply box — Align L/C/R, Horizontal Rule and Insert Button on a
// two-line answer to an email. `toolbar` makes the SET a caller's choice while
// leaving every command in one place.
//
// ## `toolbar={null}` is the full set
//
// `null` is the default, and it means DEFAULT_TOOLBAR — exactly the keys this
// component has always rendered, in exactly the order it rendered them. Every
// existing call site passes nothing and is unchanged, in behaviour and in
// markup. That is the whole reason the default is a preset rather than a flag.
//
// ## Divider semantics
//
// A '|' entry renders a ToolbarDivider. Dividers are COLLAPSED after gating:
// leading, trailing and doubled dividers are dropped. This is not cosmetic —
// it is what reproduces today's markup. The divider ahead of the merge-variable
// / AI group used to live INSIDE the `(showMergeVariables || showAIWriter)`
// conditional, so a surface with both flags off never saw it. Collapsing gets
// that for free, for every group, including ones a preset empties.
//
// ## Precedence: `toolbar` FILTERS, it never GRANTS
//
// `showMergeVariables` / `showAIWriter` / `showContentLibrary` remain a HARD
// gate. A preset naming 'ai' on a surface that never wired `contactId` /
// `dealId` / `accountId` would otherwise open an AI email writer with no record
// to write about. So a key renders only when the preset names it AND its gate is
// open. The flags can always take a control away; a preset can never add one.
//
// ## Unknown keys are skipped, deliberately
//
// A preset describes a surface's whole toolbar, and a surface may own controls
// this editor does not. `MAIL_TOOLBAR` names 'attach' for exactly that reason:
// attachments are `AttachmentBar`'s, rendered by `MailComposer` below the
// editor, and are not a TipTap command. Skipping means a typo'd key silently
// disappears — that is the cost, and `editorPresets.test.jsx` pins the preset
// against the key list below so the trade stays a choice rather than a surprise.

export const TOOLBAR_DIVIDER = '|';

/**
 * Every key the registry defines, in the order the toolbar has always rendered
 * them. Exported so a preset can be checked against it without mounting TipTap.
 */
export const DEFAULT_TOOLBAR = [
    'undo', 'redo', TOOLBAR_DIVIDER,
    'h1', 'h2', 'h3', TOOLBAR_DIVIDER,
    'bold', 'italic', 'underline', 'strike', 'code', TOOLBAR_DIVIDER,
    'alignLeft', 'alignCenter', 'alignRight', TOOLBAR_DIVIDER,
    'bulletList', 'orderedList', 'blockquote', 'horizontalRule', TOOLBAR_DIVIDER,
    'link', 'unlink', 'image', 'contentLibrary', 'voice', TOOLBAR_DIVIDER,
    'emailButton', TOOLBAR_DIVIDER,
    'mergeVariables', 'ai',
];

/** The control keys, without the dividers — what a preset may name. */
export const TOOLBAR_KEYS = DEFAULT_TOOLBAR.filter(k => k !== TOOLBAR_DIVIDER);

/**
 * Resolve a preset to the keys that will actually render.
 *
 * Pure, and separate from the registry on purpose: the registry has to be built
 * inside the component (every command closes over `editor`, and several over
 * component state), so this is the only part of the mechanism a test can reach
 * without standing up a real editor.
 *
 * `canRender(key)` answers "is there a control for this key, and is its gate
 * open" — it is what makes unknown keys and gated-off keys vanish BEFORE the
 * dividers around them are collapsed.
 */
export function resolveToolbarKeys(toolbar, canRender) {
    const preset = Array.isArray(toolbar) ? toolbar : DEFAULT_TOOLBAR;
    const out = [];
    for (const key of preset) {
        if (key === TOOLBAR_DIVIDER) {
            // Never leading, never doubled.
            if (out.length === 0 || out[out.length - 1] === TOOLBAR_DIVIDER) continue;
            out.push(key);
            continue;
        }
        if (canRender(key)) out.push(key);
    }
    // Never trailing.
    while (out.length > 0 && out[out.length - 1] === TOOLBAR_DIVIDER) out.pop();
    return out;
}

// ─── Main Editor ────────────────────────────────────────────────────────────

export default function RichTextEditor({
    content = '',
    onChange,
    placeholder = 'Start writing your content...',
    editable = true,
    className = '',
    minHeight = '200px',
    showMergeVariables = false,
    showAIWriter = false,
    showContentLibrary = false,
    // An array of registry keys (see DEFAULT_TOOLBAR) picking WHICH controls
    // render, and in what order; '|' renders a divider. `null` — the default,
    // and what every pre-existing call site passes — means the full set.
    // It filters only: the show* flags above stay a hard gate. See the
    // "Toolbar presets" block above for the full contract.
    toolbar = null,
    /**
     * `boxed` (the default) — the bordered, rounded card every existing call
     * site draws. `flush` — no border, no radius, a white toolbar and an editor
     * body that GROWS to fill its flex parent. The full-page mail composer
     * needs it: the design draws the toolbar as one more hairline row in a
     * continuous stack, and the body as the rest of the panel. The bordered
     * card cannot express either — `className` can flatten the outer box, but
     * the toolbar's tint and the body's fixed padding are internal.
     */
    variant = 'boxed',
    /**
     * Replaces the editor body's padding class. Only for a caller whose rows
     * share a gutter the variant's fixed padding does not match: the thread
     * reply cards (#659) pad every row 14px.
     */
    bodyClassName,
    /** Replaces the flush toolbar's padding + rule classes, for the same callers. */
    toolbarClassName,
    onImageUpload = null,
    contactId = null,
    dealId = null,
    accountId = null,
    onSubjectGenerated = null,
}) {
    const [showAIPanel, setShowAIPanel] = useState(false);
    const [showContentPicker, setShowContentPicker] = useState(false);
    const [showButtonConfig, setShowButtonConfig] = useState(false);
    const editor = useEditor({
        // #597 — the schema moved to ./richTextSchema so lib/editorSafeHtml's
        // fixed-point test can assert against the SAME extension list this
        // composer runs, instead of a copy that could drift from it.
        extensions: buildComposerExtensions({ placeholder }),
        content,
        editable,
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        // #623 — paste sanitisation lives with the schema; see richTextSchema.js.
        editorProps: buildComposerEditorProps(),
    });

    // Update editor content when the content prop changes externally
    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content, false);
        }
    }, [content, editor]);

    const setLink = useCallback(() => {
        if (!editor) return;
        
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Enter URL:', previousUrl);

        if (url === null) return;

        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }

        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }, [editor]);

    const fileInputRef = React.useRef(null);

    const addImage = useCallback(() => {
        if (!editor) return;

        if (onImageUpload) {
            // Use file picker when upload callback is provided
            fileInputRef.current?.click();
        } else {
            const url = window.prompt('Enter image URL:');
            if (url) {
                editor.chain().focus().setImage({ src: url }).run();
            }
        }
    }, [editor, onImageUpload]);

    const handleImageFileSelected = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file || !editor || !onImageUpload) return;
        e.target.value = '';
        try {
            const url = await onImageUpload(file);
            if (url) {
                editor.chain().focus().setImage({ src: url }).run();
            }
        } catch {}
    }, [editor, onImageUpload]);

    /**
     * The toolbar registry: key -> { gate, when, render }.
     *
     * It CANNOT be a module-level constant. Every command closes over `editor`,
     * and `link` / `image` / `emailButton` / `contentLibrary` / `ai` close over
     * component state and callbacks as well — so the table is built here and
     * memoised on the things it captures.
     *
     * `render` is a function returning a node, not `{ icon, title, onClick }`,
     * because three entries are not ToolbarButtons at all: `voice` is a
     * VoiceButton, `mergeVariables` is a MergeVariableDropdown, and
     * `emailButton` is a positioned wrapper around a button and its popover.
     *
     * `gate` is the hard show* flag; `when` is a per-render condition (only
     * `unlink`, which has always appeared solely while the caret is in a link).
     *
     * Built BEFORE the `!editor` early return below — hooks may not be skipped.
     * Nothing here dereferences `editor` at build time; the closures run only
     * when the toolbar renders, which is after that return.
     */
    const toolbarRegistry = useMemo(() => ({
        undo: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
                    <Undo className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        redo: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
                    <Redo className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        h1: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="Heading 1">
                    <Heading1 className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        h2: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2">
                    <Heading2 className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        h3: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="Heading 3">
                    <Heading3 className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        bold: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
                    <Bold className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        italic: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
                    <Italic className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        underline: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Underline">
                    <UnderlineIcon className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        strike: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Strikethrough">
                    <Strikethrough className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        code: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleCode().run()} isActive={editor.isActive('code')} title="Inline Code">
                    <Code className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        alignLeft: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align Left">
                    <AlignLeft className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        alignCenter: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align Center">
                    <AlignCenter className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        alignRight: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align Right">
                    <AlignRight className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        bulletList: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
                    <List className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        orderedList: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
                    <ListOrdered className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        blockquote: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Quote">
                    <Quote className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        horizontalRule: {
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
                    <Minus className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        link: {
            render: key => (
                <ToolbarButton key={key} onClick={setLink} isActive={editor.isActive('link')} title="Add Link">
                    <LinkIcon className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        // Has always been visible only while the caret sits in a link.
        unlink: {
            when: () => editor.isActive('link'),
            render: key => (
                <ToolbarButton key={key} onClick={() => editor.chain().focus().unsetLink().run()} title="Remove Link">
                    <Unlink className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        image: {
            render: key => (
                <ToolbarButton key={key} onClick={addImage} title="Add Image">
                    <ImageIcon className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        contentLibrary: {
            gate: showContentLibrary,
            render: key => (
                <ToolbarButton key={key} onClick={() => setShowContentPicker(true)} title="Content Library">
                    <Library className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
        // Not a ToolbarButton.
        voice: {
            render: key => (
                <VoiceButton
                    key={key}
                    onTranscript={t => {
                        if (editor) {
                            editor.chain().focus().insertContent(t + ' ').run();
                        }
                    }}
                />
            ),
        },
        // Not a ToolbarButton either — a positioned wrapper holding the button
        // and the popover it anchors.
        emailButton: {
            render: key => (
                <div key={key} className="relative">
                    <ToolbarButton
                        onClick={() => setShowButtonConfig(!showButtonConfig)}
                        isActive={showButtonConfig || editor.isActive('emailButton')}
                        title="Insert Button"
                    >
                        <RectangleHorizontal className="w-4 h-4" />
                    </ToolbarButton>
                    {showButtonConfig && (
                        <EmailButtonPopover editor={editor} onClose={() => setShowButtonConfig(false)} />
                    )}
                </div>
            ),
        },
        // Nor this one.
        mergeVariables: {
            gate: showMergeVariables,
            render: key => (
                <MergeVariableDropdown
                    key={key}
                    onInsert={varStr => editor.chain().focus().insertContent(varStr).run()}
                />
            ),
        },
        ai: {
            gate: showAIWriter,
            render: key => (
                <ToolbarButton key={key} onClick={() => setShowAIPanel(!showAIPanel)} isActive={showAIPanel} title="AI Email Writer">
                    <Sparkles className="w-4 h-4" />
                </ToolbarButton>
            ),
        },
    }), [
        editor, setLink, addImage,
        showContentLibrary, showMergeVariables, showAIWriter,
        // The two panel toggles are captured for their own active state.
        showButtonConfig, showAIPanel,
    ]);

    const flush = variant === 'flush';
    const shellCls = flush
        ? 'flex flex-col'
        : 'border border-stone-200 rounded-xl overflow-hidden';

    if (!editor) {
        return (
            <div className={cn(shellCls, className)}>
                <div className="p-4 text-center text-stone-400" style={{ minHeight }}>
                    Loading editor...
                </div>
            </div>
        );
    }

    // `toolbar` FILTERS; the show* flags GATE. A key renders only when the preset
    // names it, the registry has it, and its gate is open — so a preset can never
    // surface the AI writer on a surface that did not wire a record for it.
    const canRenderToolbarKey = key => {
        const entry = toolbarRegistry[key];
        if (!entry) return false;                       // e.g. 'attach' — not ours
        if (entry.gate === false) return false;         // hard show* gate
        if (entry.when && !entry.when()) return false;  // per-render condition
        return true;
    };
    const toolbarKeys = resolveToolbarKeys(toolbar, canRenderToolbarKey);

    return (
        <div className={cn(shellCls, className)}>
            {/* Toolbar — WHICH keys, from `toolbar` (null = the full set);
                HOW each renders, from `toolbarRegistry` above. */}
            {editable && (
                <div className={cn(
                    'flex flex-wrap items-center gap-0.5 border-b',
                    toolbarClassName ?? (flush
                        ? 'px-4 py-1.5 border-[#EDEBE6] bg-white'
                        : 'p-2 border-stone-200 bg-stone-50'),
                )}>
                    {toolbarKeys.map((key, i) => (
                        key === TOOLBAR_DIVIDER
                            ? <ToolbarDivider key={`divider-${i}`} />
                            : toolbarRegistry[key].render(key)
                    ))}
                </div>
            )}

            {/* AI Email Writer Panel */}
            {showAIPanel && showAIWriter && (
                <AIEmailWriter
                    contactId={contactId}
                    dealId={dealId}
                    accountId={accountId}
                    existingDraft={content}
                    onInsert={({ subject, body }) => {
                        editor.commands.setContent(body);
                        if (onSubjectGenerated && subject) onSubjectGenerated(subject);
                        setShowAIPanel(false);
                    }}
                    onClose={() => setShowAIPanel(false)}
                />
            )}

            {/* Content Library Picker */}
            {showContentLibrary && (
                <ContentPicker
                    open={showContentPicker}
                    onOpenChange={setShowContentPicker}
                    mode="insert"
                    onSelect={(asset) => {
                        // Prefer proxy_url (provider mode) over url (legacy mode)
                        // CRITICAL: For email body content, always prefer proxy_url when available.
                        // Emails are long-lived — presigned URLs expire in 1 hour.
                        const assetUrl = asset.proxy_url || asset.url;
                        let html = '';

                        if (asset.file_type === 'image' || asset.fileType === 'image') {
                            html = `<img src="${assetUrl}" alt="${asset.name}" style="max-width: 100%; height: auto;" />`;
                        } else if (asset.file_type === 'video' || asset.fileType === 'video') {
                            const externalUrl = asset.external_url || asset.externalUrl;
                            const provider = asset.external_provider || asset.video_provider;
                            if (externalUrl) {
                                const embedUrl = getVideoEmbedUrl(externalUrl, provider);
                                if (embedUrl) {
                                    html = `<iframe src="${embedUrl}" width="640" height="360" frameborder="0" allowfullscreen style="max-width: 100%;"></iframe>`;
                                }
                            }
                            if (!html) {
                                html = `<video controls width="640" style="max-width: 100%;"><source src="${assetUrl}"></video>`;
                            }
                        } else {
                            html = `<a href="${assetUrl}" target="_blank" rel="noopener">${asset.name}</a>`;
                        }

                        if (editor && html) {
                            editor.chain().focus().insertContent(html).run();
                        }
                    }}
                />
            )}

            {/* Editor Content */}
            <div
                className={cn('bg-white', bodyClassName ?? (flush ? 'flex-1 px-6 py-5' : 'p-4'))}
                style={{ minHeight }}
            >
                <EditorContent editor={editor} />
            </div>

            {/* Styles for the editor */}
            <style>{`
                .ProseMirror {
                    outline: none;
                }
                .ProseMirror p.is-editor-empty:first-child::before {
                    content: attr(data-placeholder);
                    float: left;
                    color: #9ca3af;
                    pointer-events: none;
                    height: 0;
                }
                .ProseMirror h1 {
                    font-size: 1.875rem;
                    font-weight: 700;
                    line-height: 1.2;
                    margin-bottom: 0.5rem;
                    color: #1e293b;
                }
                .ProseMirror h2 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    line-height: 1.3;
                    margin-bottom: 0.5rem;
                    color: #1e293b;
                }
                .ProseMirror h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    line-height: 1.4;
                    margin-bottom: 0.5rem;
                    color: #1e293b;
                }
                .ProseMirror p {
                    margin-bottom: 0.75rem;
                    color: #475569;
                }
                .ProseMirror ul, .ProseMirror ol {
                    padding-left: 1.5rem;
                    margin-bottom: 0.75rem;
                }
                .ProseMirror ul {
                    list-style-type: disc;
                }
                .ProseMirror ol {
                    list-style-type: decimal;
                }
                .ProseMirror li {
                    margin-bottom: 0.25rem;
                }
                .ProseMirror blockquote {
                    border-left: 3px solid #6366f1;
                    padding-left: 1rem;
                    margin: 1rem 0;
                    color: #64748b;
                    font-style: italic;
                }
                .ProseMirror code {
                    background: #f1f5f9;
                    padding: 0.125rem 0.375rem;
                    border-radius: 0.25rem;
                    font-size: 0.875rem;
                    font-family: monospace;
                    color: #6366f1;
                }
                .ProseMirror pre {
                    background: #1e293b;
                    color: #e2e8f0;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    overflow-x: auto;
                    margin-bottom: 0.75rem;
                }
                .ProseMirror pre code {
                    background: transparent;
                    color: inherit;
                    padding: 0;
                }
                .ProseMirror hr {
                    border: none;
                    border-top: 2px solid #e2e8f0;
                    margin: 1.5rem 0;
                }
                .ProseMirror img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 0.5rem;
                }
                .ProseMirror div[data-email-button] {
                    margin: 16px 0;
                    cursor: pointer;
                    position: relative;
                }
                .ProseMirror div[data-email-button].ProseMirror-selectednode {
                    outline: 2px solid #3b82f6;
                    outline-offset: 4px;
                    border-radius: 4px;
                }
                .ProseMirror div[data-email-button] a {
                    pointer-events: none;
                }
            `}</style>

            {/* Hidden file input for image upload */}
            {onImageUpload && (
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageFileSelected}
                />
            )}
        </div>
    );
}

// Read-only version for public display
export function RichTextViewer({ content, className = '' }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
            }),
            Underline,
            Link.configure({
                openOnClick: true,
                HTMLAttributes: {
                    class: 'text-stone-700 underline hover:text-stone-800',
                },
            }),
            Image.configure({
                HTMLAttributes: {
                    class: 'max-w-full h-auto rounded-lg my-4',
                },
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
        ],
        content,
        editable: false,
    });

    if (!editor) {
        return <div className="text-stone-400">Loading content...</div>;
    }

    return (
        <div className={cn('prose prose-sm max-w-none', className)}>
            <EditorContent editor={editor} />
            <style>{`
                .ProseMirror h1 { font-size: 1.875rem; font-weight: 700; color: #1e293b; margin-bottom: 0.5rem; }
                .ProseMirror h2 { font-size: 1.5rem; font-weight: 600; color: #1e293b; margin-bottom: 0.5rem; }
                .ProseMirror h3 { font-size: 1.25rem; font-weight: 600; color: #1e293b; margin-bottom: 0.5rem; }
                .ProseMirror p { margin-bottom: 0.75rem; color: #475569; }
                .ProseMirror ul { list-style-type: disc; padding-left: 1.5rem; margin-bottom: 0.75rem; }
                .ProseMirror ol { list-style-type: decimal; padding-left: 1.5rem; margin-bottom: 0.75rem; }
                .ProseMirror blockquote { border-left: 3px solid #6366f1; padding-left: 1rem; color: #64748b; font-style: italic; }
                .ProseMirror code { background: #f1f5f9; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-family: monospace; color: #6366f1; }
            `}</style>
        </div>
    );
}
